import { Layout } from "@/components/Layout";
import { Link } from "react-router-dom";

const Refund = () => {
  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-2">Refund &amp; Cancellation Policy</h1>
            <p className="text-sm sm:text-base text-muted-foreground mb-6 sm:mb-8">Last updated: December 23, 2024</p>

            <div className="space-y-4 sm:space-y-6 lg:space-y-8">
              <section className="bg-card rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-border">
                <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-3 sm:mb-4">1. Subscription Cancellation</h2>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-3 sm:mb-4">
                  You may cancel your Pharmasoft CS Director subscription at any time through your account settings or by contacting our support team. Upon cancellation:
                </p>
                <ul className="list-disc list-inside text-sm sm:text-base text-muted-foreground space-y-1.5 sm:space-y-2">
                  <li>Your subscription will remain active until the end of the current billing period (annual cycle)</li>
                  <li>You will continue to have access to all features until the subscription expires</li>
                  <li>No further charges will be made after cancellation</li>
                  <li>Your data will be retained for 30 days after subscription expiry, after which it may be permanently deleted</li>
                </ul>
              </section>

              <section className="bg-card rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-border">
                <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-3 sm:mb-4">2. Refund Policy</h2>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-3 sm:mb-4">We offer refunds under the following conditions:</p>
                <ul className="list-disc list-inside text-sm sm:text-base text-muted-foreground space-y-1.5 sm:space-y-2">
                  <li>
                    <span className="text-foreground font-semibold">Within 7 days of purchase:</span> Full refund if you are not satisfied with the service
                  </li>
                  <li>
                    <span className="text-foreground font-semibold">Technical issues:</span> Pro-rated refund if the service is unavailable for extended periods due to
                    our fault
                  </li>
                  <li>
                    <span className="text-foreground font-semibold">Duplicate payments:</span> Full refund for any accidental duplicate charges
                  </li>
                </ul>
              </section>

              <section className="bg-card rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-border">
                <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-3 sm:mb-4">3. Non-Refundable Situations</h2>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-3 sm:mb-4">Refunds will not be provided in the following cases:</p>
                <ul className="list-disc list-inside text-sm sm:text-base text-muted-foreground space-y-1.5 sm:space-y-2">
                  <li>Requests made after 7 days from the date of purchase</li>
                  <li>Partial month/year usage after the 7-day period</li>
                  <li>Service disruptions caused by user&apos;s own technical issues or internet connectivity</li>
                  <li>Failure to use the service during the subscription period</li>
                  <li>Plan downgrades (difference in plan prices is not refundable)</li>
                </ul>
              </section>

              <section className="bg-card rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-border">
                <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-3 sm:mb-4">4. Plan Upgrades</h2>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  When upgrading from one plan to another (e.g., Basic to Core, or Core to Advanced), you will be charged the pro-rated difference for the remaining
                  subscription period. The upgraded features will be available immediately upon successful payment.
                </p>
              </section>

              <section className="bg-card rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-border">
                <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-3 sm:mb-4">5. How to Request a Refund</h2>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-3 sm:mb-4">To request a refund, please follow these steps:</p>
                <ol className="list-decimal list-inside text-sm sm:text-base text-muted-foreground space-y-1.5 sm:space-y-2">
                  <li>
                    Contact our support team via the{" "}
                    <Link to="/contact" className="text-primary hover:underline">
                      Contact Us
                    </Link>{" "}
                    page
                  </li>
                  <li>Provide your registered email address and order/transaction ID</li>
                  <li>Clearly state the reason for your refund request</li>
                  <li>Our team will review your request within 2-3 business days</li>
                </ol>
              </section>

              <section className="bg-card rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-border">
                <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-3 sm:mb-4">6. Refund Processing</h2>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-3 sm:mb-4">Once a refund is approved:</p>
                <ul className="list-disc list-inside text-sm sm:text-base text-muted-foreground space-y-1.5 sm:space-y-2">
                  <li>The refund will be processed within 5-7 business days</li>
                  <li>The amount will be credited to the original payment method</li>
                  <li>You will receive an email confirmation once the refund is processed</li>
                  <li>GST paid on the original transaction will also be refunded proportionately</li>
                </ul>
              </section>

              <section className="bg-card rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-border">
                <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-3 sm:mb-4">7. Contact Us</h2>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  For any questions regarding our refund and cancellation policy, please{" "}
                  <Link to="/contact" className="text-primary hover:underline">
                    contact us
                  </Link>
                  . Our support team is available to assist you with any concerns.
                </p>
              </section>
            </div>
          </div>
        </main>

        <footer className="border-t border-border mt-8 sm:mt-12 lg:mt-16 py-6 sm:py-8">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 md:gap-6 text-xs sm:text-sm text-muted-foreground">
              <Link to="/about" className="hover:text-foreground transition-colors">
                About Us
              </Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">
                Terms &amp; Conditions
              </Link>
              <Link to="/privacy" className="hover:text-foreground transition-colors">
                Privacy Policy
              </Link>
              <Link to="/refund" className="hover:text-foreground transition-colors">
                Refund &amp; Cancellation
              </Link>
              <Link to="/contact" className="hover:text-foreground transition-colors">
                Contact Us
              </Link>
            </div>
            <p className="text-center text-sm text-muted-foreground mt-4">
              © {new Date().getFullYear()}{" "}
              <a
                href="https://kireetisoft.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-foreground transition-colors underline"
              >
                KIREETI SOFT TECHNOLOGIES LIMITED.
              </a>{" "}
              All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </Layout>
  );
};

export default Refund;

